import { alpha, createTheme, darken, lighten } from "@mui/material/styles";
import { DrawerTransition } from "../feedback/DrawerTransition.js";
import "./theme.types.js"; // Import type augmentations
/**
 * The ShipMyApp design system's colour tokens (the "ConferHall Responsive
 * Design" canvas installs them as `ds/shipmyapp/tokens.json`). Contrast on
 * the light surface: heading 10.6:1, muted 5.9:1, label 6.3:1,
 * borderStrong 3.3:1 — the last is for control outlines only, never text.
 */
const TOKENS = {
    light: {
        surface: "#ffffff",
        surfaceEmphasis: "#eef0fd",
        surfaceTint: "#f5f4f1",
        heading: "#2b2a7a",
        body: "#1f2130",
        muted: "#5b6075",
        label: "#4f46e5",
        accentStrong: "#4338ca",
        border: "#dde0fa",
        borderStrong: "#7f85cf",
        success: "#1f7a4d",
        warning: "#8a5a0a",
        danger: "#b42318",
    },
    dark: {
        surface: "#121327",
        surfaceEmphasis: "#1c1e3e",
        surfaceTint: "#1a1a1f",
        heading: "#c9c7ff",
        body: "#e4e5f0",
        muted: "#a4a8bd",
        label: "#8b85ff",
        accentStrong: "#8b85ff",
        border: "#2f3261",
        borderStrong: "#6b70b8",
        success: "#5fcf93",
        warning: "#e6b352",
        danger: "#f58a7f",
    },
};
const DISPLAY_FONT = '"Inter Tight", "Inter", system-ui, sans-serif';
const SANS_FONT = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
const MONO_FONT = '"JetBrains Mono", ui-monospace, Menlo, monospace';
const display = (fontSize, lineHeight, letterSpacing = "-0.01em") => ({
    fontFamily: DISPLAY_FONT,
    fontWeight: 600,
    fontSize,
    lineHeight,
    letterSpacing,
});
/**
 * Creates a theme with the specified mode (light or dark)
 * Preserves all component overrides and customizations
 */
export const createAppTheme = (mode = "light", overrides) => {
    const t = TOKENS[mode];
    const tenantPrimary = overrides?.primary ?? null;
    // A tenant's own colour replaces the indigo everywhere the indigo carries
    // meaning (accent, labels, headings, the pale tint), so a white-label PWA
    // never shows ConferHall's brand next to its own.
    const heading = tenantPrimary
        ? mode === "light"
            ? darken(tenantPrimary, 0.45)
            : lighten(tenantPrimary, 0.6)
        : t.heading;
    const surfaceEmphasis = tenantPrimary
        ? alpha(tenantPrimary, mode === "light" ? 0.1 : 0.18)
        : t.surfaceEmphasis;
    return createTheme({
        palette: {
            mode,
            primary: tenantPrimary
                ? { main: tenantPrimary, lighter: surfaceEmphasis }
                : {
                    main: mode === "light" ? t.label : "#6e67f0",
                    light: mode === "light" ? "#818cf8" : t.label,
                    dark: t.accentStrong,
                    lighter: t.surfaceEmphasis,
                    contrastText: mode === "light" ? "#ffffff" : "#121327",
                },
            secondary: overrides?.secondary
                ? { main: overrides.secondary }
                : { main: t.heading, contrastText: mode === "light" ? "#ffffff" : "#121327" },
            success: { main: t.success, lighter: alpha(t.success, 0.1) },
            warning: { main: t.warning, lighter: alpha(t.warning, 0.1) },
            error: { main: t.danger, lighter: alpha(t.danger, 0.1) },
            info: { main: t.label },
            // A tenant background colour is honoured in light mode only; dark mode
            // keeps the readable dark surfaces.
            background: {
                default: overrides?.background && mode === "light" ? overrides.background : t.surface,
                paper: t.surface,
            },
            text: { primary: t.body, secondary: t.muted, disabled: alpha(t.muted, 0.6) },
            divider: t.border,
            heading,
            surfaceEmphasis,
            surfaceTint: t.surfaceTint,
            borderStrong: t.borderStrong,
        },
        typography: {
            fontFamily: SANS_FONT,
            h1: { ...display("2.75rem", "1.08", "-0.02em"), color: heading },
            h2: { ...display("2.25rem", "1.1", "-0.02em"), color: heading },
            h3: { ...display("1.75rem", "1.15", "-0.02em"), color: heading },
            h4: { ...display("1.5rem", "1.25"), color: heading },
            h5: { ...display("1.25rem", "1.3"), color: heading },
            h6: { ...display("1.125rem", "1.35"), color: heading },
            overline: {
                fontSize: "0.75rem",
                fontWeight: 600,
                lineHeight: 1.5,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
            },
            button: { textTransform: "none", fontWeight: 600 },
            mono: { fontFamily: MONO_FONT, fontWeight: 500 },
        },
        shape: { borderRadius: 10 },
        spacing: 8,
        components: {
            MuiCssBaseline: {
                // Scrollbars as hairlines in the theme's muted tone, on every
                // scroller in the app: the desk's live-text columns are the
                // loudest case, where a chrome-grey bar fought the design.
                styleOverrides: {
                    body: { WebkitFontSmoothing: "antialiased" },
                    "*": {
                        scrollbarWidth: "thin",
                        scrollbarColor: `${alpha(t.muted, 0.35)} transparent`,
                    },
                    "*::-webkit-scrollbar": { width: 6, height: 6 },
                    "*::-webkit-scrollbar-track": { background: "transparent" },
                    "*::-webkit-scrollbar-thumb": {
                        background: alpha(t.muted, 0.35),
                        borderRadius: 999,
                        border: "none",
                    },
                    "*::-webkit-scrollbar-thumb:hover": { background: alpha(t.muted, 0.6) },
                    // iOS zooms the page in on a field whose text is under 16px,
                    // and the layout stays zoomed after: on touch screens every
                    // field types at 16px. Pinch-zoom is left alone.
                    "@media (pointer: coarse)": {
                        "input, textarea, select": { fontSize: "16px !important" },
                    },
                },
            },
            MuiTypography: {
                defaultProps: { variantMapping: { mono: "span" } },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        minHeight: 44,
                        borderRadius: 10,
                        transition: "background .2s ease, color .2s ease",
                    },
                    sizeLarge: { minHeight: 48, padding: "0 22px", fontSize: "0.9375rem" },
                    outlined: { borderColor: t.borderStrong },
                },
            },
            MuiIconButton: {
                styleOverrides: { root: { borderRadius: 10 } },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: { borderRadius: 10 },
                    notchedOutline: { borderColor: t.borderStrong },
                },
            },
            MuiTextField: {
                defaultProps: { variant: "outlined", size: "small" },
            },
            MuiPaper: {
                styleOverrides: { root: { backgroundImage: "none" } },
            },
            MuiCard: {
                defaultProps: { elevation: 0 },
                styleOverrides: { root: { borderRadius: 16, border: `1px solid ${t.border}` } },
            },
            MuiChip: {
                styleOverrides: { root: { borderRadius: 6, fontWeight: 600 } },
            },
            MuiAlert: {
                styleOverrides: { root: { borderRadius: 10 } },
            },
            // Dialogs open as drawers: docked to the bottom of a phone, to the
            // right edge from `md` up, full height there and as wide as the
            // dialog's own maxWidth allows (DrawerTransition slides them in).
            MuiDialog: {
                defaultProps: { slots: { transition: DrawerTransition } },
                styleOverrides: {
                    container: ({ theme }) => ({
                        alignItems: "flex-end",
                        [theme.breakpoints.up("md")]: { alignItems: "stretch", justifyContent: "flex-end" },
                    }),
                    paper: ({ theme }) => ({
                        margin: 0,
                        width: "100%",
                        maxWidth: "100%",
                        maxHeight: "92dvh",
                        borderRadius: "20px 20px 0 0",
                        paddingBottom: "env(safe-area-inset-bottom)",
                        // A grab handle, so a phone's sheet reads as one.
                        "&::before": {
                            content: '""',
                            flex: "none",
                            alignSelf: "center",
                            width: 40,
                            height: 4,
                            marginTop: 8,
                            borderRadius: 2,
                            backgroundColor: t.borderStrong,
                        },
                        [theme.breakpoints.up("md")]: {
                            height: "100dvh",
                            maxHeight: "100dvh",
                            borderRadius: "20px 0 0 20px",
                            paddingBottom: 0,
                            "&::before": { display: "none" },
                        },
                    }),
                    paperWidthXs: ({ theme }) => ({ [theme.breakpoints.up("md")]: { maxWidth: 480 } }),
                    paperWidthSm: ({ theme }) => ({ [theme.breakpoints.up("md")]: { maxWidth: 600 } }),
                    paperWidthMd: ({ theme }) => ({ [theme.breakpoints.up("md")]: { maxWidth: 760 } }),
                    paperWidthLg: ({ theme }) => ({ [theme.breakpoints.up("md")]: { maxWidth: 960 } }),
                },
            },
            MuiTableCell: {
                styleOverrides: { root: { borderColor: t.border } },
            },
        },
    });
};
/**
 * Default light theme (for backwards compatibility)
 */
export const theme = createAppTheme("light");
//# sourceMappingURL=theme.config.js.map